// Versus mode: player engine + 1–3 AI engines running in lockstep.
// Garbage is exchanged on line clears according to the active VersusMode.

import { BOARD_WIDTH, TICK_MS } from '../config';
import { Engine } from '../game/engine';
import type { EngineEvent } from '../game/engine';
import type { LockResult } from '../types';
import { Bot, DIFFICULTIES, applyBotAction, type BotDifficulty } from './bot';

/** Identifier for the currently-active battle mode. Each one has its own
 *  rules in VERSUS_MODES below. */
export type VersusModeId =
  | 'duel'          // 1v1, standard rules
  | 'triple'        // 1v2, garbage from player splits across both AIs
  | 'royal'         // 1v3, free-for-all (AIs also attack each other)
  | 'cheese-brawl'  // 1v1, both sides start with garbage already on the board
  | 'sudden-death'  // 1v1, every attack is 1.5× normal damage (rounded up)
  | 'sprint-versus' // 1v1, first to 40 lines wins (no garbage exchange)
  ;

/** How an AI's outgoing damage is distributed.
 *  - 'player-only': legacy behavior; every line goes to the player.
 *  - 'split-50-50': half the damage goes to the player, half splits across
 *    the other living AIs. If no other AIs are alive, falls back to
 *    'player-only'. */
export type AiTargeting = 'player-only' | 'split-50-50';

/** Either end of a garbage exchange. AI endpoints encode the slot index. */
export type AttackEndpoint = 'player' | `ai:${number}`;

export interface VersusModeConfig {
  id: VersusModeId;
  name: string;
  /** One-liner shown in the battle select screen. */
  desc: string;
  /** How many AI opponents spawn alongside the player. */
  aiCount: 1 | 2 | 3;
  /** Multiplier applied to all garbage damage (1 = default, 1.5 = sudden death). */
  damageMul: number;
  /** Number of garbage rows pre-injected onto both sides at the start. */
  initialGarbage: number;
  /** Win condition: 'survive' = last alive wins, 'sprint40' = first to 40 lines. */
  winCondition: 'survive' | 'sprint40';
  /** If true, garbage is suppressed — used by sprint-versus where it would
   *  defeat the purpose of a clean speed race. */
  noGarbage: boolean;
  /** Targeting policy for AI-side damage. Defaults to 'player-only'. */
  aiTargeting?: AiTargeting;
}

export const VERSUS_MODES: VersusModeConfig[] = [
  { id: 'duel',          name: 'duel',          desc: 'standard 1v1. last to top out wins.',                              aiCount: 1, damageMul: 1.0, initialGarbage: 0, winCondition: 'survive', noGarbage: false },
  { id: 'triple',        name: 'triple threat', desc: '1v2. your garbage is split between both opponents.',               aiCount: 2, damageMul: 1.0, initialGarbage: 0, winCondition: 'survive', noGarbage: false },
  { id: 'royal',         name: 'royal rumble',  desc: '1v3 free-for-all. every player for themselves.',                   aiCount: 3, damageMul: 1.0, initialGarbage: 0, winCondition: 'survive', noGarbage: false, aiTargeting: 'split-50-50' },
  { id: 'cheese-brawl',  name: 'garbage brawl', desc: '1v1 with eight garbage rows pre-filled on both sides.',            aiCount: 1, damageMul: 1.0, initialGarbage: 8, winCondition: 'survive', noGarbage: false },
  { id: 'sudden-death',  name: 'sudden death',  desc: '1v1 with every attack amplified by 1.5×. dangerous.',              aiCount: 1, damageMul: 1.5, initialGarbage: 0, winCondition: 'survive', noGarbage: false },
  { id: 'sprint-versus', name: 'sprint versus', desc: '1v1 race to 40 lines. no garbage. fastest finger wins.',           aiCount: 1, damageMul: 1.0, initialGarbage: 0, winCondition: 'sprint40', noGarbage: true },
];

export function getVersusMode(id: VersusModeId): VersusModeConfig {
  return VERSUS_MODES.find((m) => m.id === id) ?? VERSUS_MODES[0]!;
}

export interface VersusOptions {
  difficulty: BotDifficulty['id'];
  modeId?: VersusModeId;
}

/** Standard versus damage table (lines sent on each clear type).
 *  Returns the raw base damage before mode-multiplier is applied. */
function damageFor(result: LockResult): number {
  if (result.cleared.length === 0) return 0;
  let base = 0;
  if (result.tSpinKind === 'full') {
    if (result.cleared.length === 1) base = 2;
    if (result.cleared.length === 2) base = 4;
    if (result.cleared.length === 3) base = 6;
  } else if (result.tSpinKind === 'mini') {
    if (result.cleared.length === 1) base = 1;
    if (result.cleared.length === 2) base = 1;
  } else {
    if (result.cleared.length === 1) base = 0;
    if (result.cleared.length === 2) base = 1;
    if (result.cleared.length === 3) base = 2;
    if (result.cleared.length === 4) base = 4;
  }
  if (result.isBackToBack) base += 1;
  if (result.isPerfectClear) base += 10;
  if (result.comboCount >= 1) {
    const comboBonus = Math.min(5, Math.floor(result.comboCount / 2));
    base += comboBonus;
  }
  return base;
}

interface AiSlot {
  index: number;        // 0 = primary AI (kept as .ai for back-compat)
  engine: Engine;
  bot: Bot;
  alive: boolean;
  garbageQueue: number;
  linesSent: number;
  /** Per-AI rotation cursor for split-targeting modes. Each outgoing
   *  line steps the cursor forward through the living-targets list, so
   *  single-line attacks land on different sides over time instead of
   *  always hitting the player. */
  splitCursor: number;
}

export class VersusController {
  player: Engine;
  /** Primary AI — kept as a direct field so existing single-AI code paths
   *  (PixiApp.aiField, achievements gating, etc.) stay backward-compatible. */
  ai: Engine;
  bot: Bot;
  /** Full slot list including the primary. ais[0].engine === this.ai. */
  ais: AiSlot[];
  mode: VersusModeConfig;
  state: 'running' | 'player-won' | 'ai-won' = 'running';
  /** Garbage queued for the player. AIs each have their own queue inside
   *  their slot. Drains slowly so the receiver has a chance to clear. */
  private playerGarbageQueue = 0;
  private garbageDrainAcc = 0;
  /** Lines sent by the player across all AIs over the run. */
  playerLinesSent = 0;
  /** Lines sent against the player by all AIs combined. */
  aiLinesSent = 0;

  /** External observers — App wires these so visual/audio fx fire on the
   *  player side and a lighter set of fx fire on each AI side. */
  onPlayerEvent: ((ev: EngineEvent) => void) | null = null;
  /** Fires for every AI's events. aiIndex disambiguates which AI emitted it. */
  onAiEvent: ((ev: EngineEvent, aiIndex: number) => void) | null = null;
  /** Fires whenever non-canceled garbage is queued at a target. `from` is
   *  the attacker, `to` is the receiver. Lines are post-cancellation, the
   *  count actually added to the receiver's queue. */
  onAttackSent: ((from: AttackEndpoint, to: AttackEndpoint, lines: number) => void) | null = null;
  onGarbageReceived: ((who: AttackEndpoint, lines: number) => void) | null = null;

  constructor(opts: VersusOptions) {
    this.mode = getVersusMode(opts.modeId ?? 'duel');
    this.player = new Engine({
      dasMs: 130, arrMs: 16, softDropFactor: 30, dcdMs: 0,
      ghostEnabled: true, initialLevel: 1, preview: 5,
      levelGravity: false, noTopout: false,
    });
    this.ais = [];
    for (let i = 0; i < this.mode.aiCount; i++) {
      const eng = new Engine({
        dasMs: 0, arrMs: 0, softDropFactor: 1000, dcdMs: 0,
        ghostEnabled: false, initialLevel: 1, preview: 5,
        levelGravity: false, noTopout: false,
      });
      const bot = new Bot(eng, DIFFICULTIES[opts.difficulty]);
      this.ais.push({ index: i, engine: eng, bot, alive: true, garbageQueue: 0, linesSent: 0, splitCursor: i });
    }
    this.ai = this.ais[0]!.engine;
    this.bot = this.ais[0]!.bot;

    // Pre-fill garbage on both sides for cheese-brawl style modes.
    const initBoard = this.mode.initialGarbage > 0
      ? (b: import('../game/board').Board) => {
        let seed = Date.now() >>> 0;
        const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };
        let lastHole = -1;
        for (let r = 0; r < this.mode.initialGarbage; r++) {
          let hole = Math.floor(rand() * BOARD_WIDTH);
          while (hole === lastHole) hole = Math.floor(rand() * BOARD_WIDTH);
          lastHole = hole;
          const y = 39 - r;
          for (let x = 0; x < BOARD_WIDTH; x++) {
            if (x !== hole) b.set(x, y, 'G');
          }
        }
      }
      : undefined;
    this.player.start({ initialLevel: 1, noTopout: false }, initBoard);
    for (const slot of this.ais) {
      slot.engine.start({ initialLevel: 1, noTopout: false }, initBoard);
    }
  }

  setDifficulty(d: BotDifficulty['id']): void {
    for (const slot of this.ais) slot.bot.setDifficulty(DIFFICULTIES[d]);
  }

  /** Per-tick update. Drives every AI + drains garbage. */
  update(dtMs: number): void {
    if (this.state !== 'running') return;
    this.player.update(dtMs);
    for (const slot of this.ais) {
      if (!slot.alive) continue;
      slot.engine.update(dtMs);
      const action = slot.bot.tick(dtMs);
      if (action) applyBotAction(slot.engine, action);
    }
    // Process events from each side.
    for (const ev of this.player.events) {
      this.onPlayerEventInternal(ev);
      try { this.onPlayerEvent?.(ev); } catch { /* observer error — swallow */ }
    }
    this.player.events.length = 0;
    for (const slot of this.ais) {
      for (const ev of slot.engine.events) {
        this.onAiEventInternal(ev, slot);
        try { this.onAiEvent?.(ev, slot.index); } catch { /* swallow */ }
      }
      slot.engine.events.length = 0;
    }
    // Sprint-versus: first to 40 lines wins, regardless of garbage.
    if (this.mode.winCondition === 'sprint40' && this.state === 'running') {
      if (this.player.stats.lines >= 40) this.state = 'player-won';
      else {
        for (const slot of this.ais) {
          if (slot.engine.stats.lines >= 40) { this.state = 'ai-won'; break; }
        }
      }
    }
    // Survive-mode win check: if all AIs are dead, player wins.
    if (this.mode.winCondition === 'survive' && this.state === 'running' && this.ais.every((s) => !s.alive)) {
      this.state = 'player-won';
    }
    this.garbageDrainAcc += dtMs;
    if (this.garbageDrainAcc > 100) this.garbageDrainAcc = 0;
  }

  /** Damage modifier: applies mode multiplier + ceil so partial lines
   *  always round up (sudden-death otherwise loses fractional damage). */
  private applyDmgMul(raw: number): number {
    if (raw <= 0) return 0;
    return Math.ceil(raw * this.mode.damageMul);
  }

  private onPlayerEventInternal(ev: EngineEvent): void {
    if (ev.type === 'lock') {
      const raw = damageFor(ev.result);
      const dmg = this.applyDmgMul(raw);
      // Cancel against player's own incoming queue first.
      const canceled = Math.min(this.playerGarbageQueue, dmg);
      this.playerGarbageQueue -= canceled;
      const remaining = this.mode.noGarbage ? 0 : (dmg - canceled);
      if (remaining > 0) {
        // Distribute across living AIs. Single AI: all to that one.
        // Multi AI: send to the AI with the smallest current queue (so
        // damage spreads instead of piling onto one opponent who's
        // already overwhelmed). Ties broken by lower index.
        const livingAis = this.ais.filter((s) => s.alive);
        if (livingAis.length > 0) {
          const split = Math.floor(remaining / livingAis.length);
          const remainder = remaining - split * livingAis.length;
          // Sort by current queue ascending so the remainder ends up on
          // the least-burdened AI first.
          const sorted = [...livingAis].sort((a, b) => a.garbageQueue - b.garbageQueue || a.index - b.index);
          for (let i = 0; i < sorted.length; i++) {
            const extra = i < remainder ? 1 : 0;
            const lines = split + extra;
            if (lines <= 0) continue;
            sorted[i]!.garbageQueue += lines;
            this.playerLinesSent += lines;
            try { this.onAttackSent?.('player', `ai:${sorted[i]!.index}`, lines); } catch { /* */ }
          }
        }
      }
      // Apply queued garbage to the player after their clear (before next
      // spawn settles). The cancel pass above just zeroed out anything
      // we'd be about to receive that this clear neutralized.
      this.applyGarbageToPlayer();
    } else if (ev.type === 'topout') {
      // Player tops out: every living AI wins (game over).
      this.state = 'ai-won';
    } else if (ev.type === 'spawn') {
      this.applyGarbageToPlayer();
    }
  }

  private onAiEventInternal(ev: EngineEvent, slot: AiSlot): void {
    if (ev.type === 'lock') {
      const raw = damageFor(ev.result);
      const dmg = this.applyDmgMul(raw);
      // AI's own incoming queue first.
      const canceled = Math.min(slot.garbageQueue, dmg);
      slot.garbageQueue -= canceled;
      const remaining = this.mode.noGarbage ? 0 : (dmg - canceled);
      if (remaining > 0) {
        const targeting = this.mode.aiTargeting ?? 'player-only';
        const otherLivingAis = this.ais.filter((s) => s.alive && s.index !== slot.index);

        if (targeting === 'split-50-50' && otherLivingAis.length > 0) {
          // Round-robin distribution per line: each AI cycles through
          // [player, ...otherLivingAis] one line at a time. Single-line
          // clears (the common case) thus visibly land on different
          // sides over a run instead of every one piling on the player.
          // Each AI keeps its own splitCursor so different AIs offset
          // their rotations, making the spread feel chaotic vs. lockstep.
          const targets: AttackEndpoint[] = ['player', ...otherLivingAis.map((s) => `ai:${s.index}` as AttackEndpoint)];
          const buckets = new Map<AttackEndpoint, number>();
          for (let i = 0; i < remaining; i++) {
            slot.splitCursor = (slot.splitCursor + 1) % targets.length;
            const t = targets[slot.splitCursor]!;
            buckets.set(t, (buckets.get(t) ?? 0) + 1);
          }
          for (const [target, lines] of buckets) {
            if (target === 'player') {
              this.playerGarbageQueue += lines;
              this.aiLinesSent += lines;
            } else {
              const idx = parseInt(target.slice(3), 10);
              const targetSlot = this.ais.find((s) => s.index === idx);
              if (targetSlot) targetSlot.garbageQueue += lines;
            }
            slot.linesSent += lines;
            try { this.onAttackSent?.(`ai:${slot.index}`, target, lines); } catch { /* */ }
          }
        } else {
          // Default / fallback (last AI standing): all to player.
          this.playerGarbageQueue += remaining;
          slot.linesSent += remaining;
          this.aiLinesSent += remaining;
          try { this.onAttackSent?.(`ai:${slot.index}`, 'player', remaining); } catch { /* */ }
        }
      }
      this.applyGarbageToAi(slot);
    } else if (ev.type === 'topout') {
      slot.alive = false;
      // Don't end the match yet — other AIs might still be alive. The
      // update() pass checks "all AIs dead" and flips to player-won.
    } else if (ev.type === 'spawn') {
      this.applyGarbageToAi(slot);
    }
  }

  private applyGarbageToPlayer(): void {
    if (this.playerGarbageQueue <= 0) return;
    const queue = this.playerGarbageQueue;
    const holeCol = Math.floor(Math.random() * BOARD_WIDTH);
    this.player.board.addGarbage(queue, holeCol);
    this.playerGarbageQueue = 0;
    try { this.onGarbageReceived?.('player', queue); } catch { /* */ }
  }

  private applyGarbageToAi(slot: AiSlot): void {
    if (slot.garbageQueue <= 0 || !slot.alive) return;
    const queue = slot.garbageQueue;
    const holeCol = Math.floor(Math.random() * BOARD_WIDTH);
    slot.engine.board.addGarbage(queue, holeCol);
    slot.garbageQueue = 0;
    try { this.onGarbageReceived?.(`ai:${slot.index}`, queue); } catch { /* */ }
  }

  /** Backward-compatible accessor used by HUD code that only knows about
   *  one AI. Sums all queues if asked about 'ai'. */
  pendingGarbageFor(who: 'player' | 'ai'): number {
    if (who === 'player') return this.playerGarbageQueue;
    return this.ais.reduce((sum, s) => sum + s.garbageQueue, 0);
  }

  pendingGarbageForAi(index: number): number {
    return this.ais[index]?.garbageQueue ?? 0;
  }

  // Keep the tick rate constant import alive (re-exports needed elsewhere).
  static TICK_MS = TICK_MS;
}

export const VERSUS_DIFFICULTIES = DIFFICULTIES;
